import React from "react";
import ReactDOM from "react-dom/client";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Box,
    Fade,
} from "@mui/material";
import { StyledEngineProvider } from "@mui/material/styles";
import ThemeProvider from "../../theme";
import { LocalizationProvider } from "../../i18n/LocalizationProvider";
import Iconify from "../iconify";

const NotesPromptDialog = ({ onResolve, onReject, options = {} }) => {
    const [notes, setNotes] = React.useState("");
    const [open, setOpen] = React.useState(true);

    const {
        title = "Add Notes",
        message = "Please enter any notes for this action (optional):",
        placeholder = "Enter notes here...",
        confirmText = "Confirm",
        cancelText = "Cancel",
    } = options;

    const handleConfirm = () => {
        setOpen(false);
        onResolve(notes);
    };

    const handleCancel = () => {
        setOpen(false);
        onReject(new Error("Notes prompt cancelled"));
    };

    const handleKeyDown = (event) => {
        // Allow Ctrl+Enter or Cmd+Enter to submit
        if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            handleConfirm();
        }
    };

    return (
        <StyledEngineProvider injectFirst>
            <LocalizationProvider>
                <ThemeProvider>
                    <Dialog
                        open={open}
                        onClose={handleCancel}
                        maxWidth="sm"
                        fullWidth
                        TransitionComponent={Fade}
                        transitionDuration={{ enter: 400, exit: 300 }}
                        PaperProps={{
                            sx: (theme) => ({
                                boxShadow:
                                    theme.customShadows?.dialog ||
                                    theme.shadows[24],
                                overflow: "visible",
                            }),
                        }}
                        sx={{
                            "& .MuiBackdrop-root": {
                                backgroundColor: "rgba(0, 0, 0, 0.6)",
                                backdropFilter: "blur(4px)",
                            },
                        }}
                    >
                        <DialogTitle
                            sx={(theme) => ({
                                pb: 1,
                                color: theme.palette.text.primary,
                                fontWeight: 600,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            })}
                        >
                            <Box
                                sx={(theme) => ({
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 24,
                                    height: 24,
                                    backgroundColor: theme.palette.primary.lighter,
                                    color: theme.palette.primary.main,
                                })}
                            >
                                <Iconify icon="eva:edit-2-outline" width={14} />
                            </Box>
                            {title}
                        </DialogTitle>
                        <DialogContent sx={{ pb: 1 }}>
                            <DialogContentText
                                sx={(theme) => ({
                                    mb: 2.5,
                                    color: theme.palette.text.secondary,
                                    fontSize: "0.95rem",
                                    lineHeight: 1.5,
                                })}
                            >
                                {message}
                            </DialogContentText>

                        <TextField
                            autoFocus
                            margin="dense"
                            label="Notes"
                            fullWidth
                            variant="outlined"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={placeholder}
                            multiline
                            rows={3}
                            helperText="Press Ctrl+Enter to submit quickly"
                            sx={(theme) => ({
                                "& .MuiOutlinedInput-root": {
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                        {
                                            borderColor:
                                                theme.palette.primary.main,
                                        },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                        {
                                            borderColor:
                                                theme.palette.primary.main,
                                            borderWidth: 2,
                                        },
                                },
                                "& .MuiFormHelperText-root": {
                                    color: theme.palette.text.disabled,
                                    fontSize: "0.8rem",
                                },
                            })}
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
                        <Button
                            onClick={handleCancel}
                            variant="outlined"
                            sx={(theme) => ({
                                borderColor: theme.palette.grey[300],
                                color: theme.palette.grey[600],
                                "&:hover": {
                                    borderColor: theme.palette.grey[400],
                                    backgroundColor: theme.palette.grey[50],
                                },
                            })}
                        >
                            {cancelText}
                        </Button>
                        <Button
                            onClick={handleConfirm}
                            variant="contained"
                            color="primary"
                            sx={(theme) => ({
                                minWidth: 100,
                                boxShadow:
                                    theme.customShadows?.primary ||
                                    theme.shadows[8],
                                "&:hover": {
                                    boxShadow:
                                        theme.customShadows?.primary ||
                                        theme.shadows[12],
                                },
                            })}
                        >
                            {confirmText}
                        </Button>
                    </DialogActions>
                    </Dialog>
                </ThemeProvider>
            </LocalizationProvider>
        </StyledEngineProvider>
    );
};

export const showNotesPrompt = (options = {}) => {
    return new Promise((resolve, reject) => {
        // Create a container for the dialog
        const container = document.createElement("div");
        document.body.appendChild(container);

        const root = ReactDOM.createRoot(container);

        const cleanup = () => {
            // Cleanup after dialog closes
            setTimeout(() => {
                root.unmount();
                document.body.removeChild(container);
            }, 300); // Small delay to allow dialog close animation
        };

        const handleResolve = (notes) => {
            cleanup();
            resolve(notes);
        };

        const handleReject = (error) => {
            cleanup();
            reject(error);
        };

        root.render(
            <NotesPromptDialog
                onResolve={handleResolve}
                onReject={handleReject}
                options={options}
            />
        );
    });
};
